import { useTasks } from '@app/context/TaskContext';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { computeWeightedProgress } from '@app/utils/progress';
import { AlertDialog, Spinner, ThreeDots } from '@components/ui';
import { ThreeDotItem } from '@components/ui/ThreeDots/ThreeDots';
import { useAssignDpoMutation } from '@dieterApi/dpo/useAssignDpoMutation';
import { useActivateTenantBillingMutation } from '@dieterApi/user/useActivateTenantBillingMutation';
import { useDeleteTenantMutation } from '@dieterApi/user/useUserDeleteTenantMutation';
import { ApplicationType, UserRole } from '@dieterApi/user/useUserQuery';
import { useSwitchTenantMutation } from '@dieterApi/user/useUserSwitchTenantMutation';
import { useUserTenantsQuery } from '@dieterApi/user/useUserTenantsQuery';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Chip, Skeleton, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MUIDataTable, { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useKlausSyncEnabled, useUnlinkCompanyFromKlausMutation } from '@dieterApi/company/useKlausLinkMutations';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { KlausLinkDialog } from './KlausLinkDialog';
import { MaxReachedModal } from './MaxReachedModal';
import { MultiTenantInfo } from './MultiTenantInfo';
import { MutateTenantModal } from './MutateTenantModal';
import './multitenanttable.sass';

// Component for DPO assignment cell - shows indicator only
function DpoAssignmentCell({ companyId, onClick }: { companyId: string; onClick: () => void }) {
  const { t } = useTranslation();
  const { user } = useUserWithCompany();

  const company = user?.companies.find((c) => c.id === companyId);
  const currentDpoUserId = company?.assignedDpoUserId || '';
  const hasDpo = !!currentDpoUserId;

  return (
    <div className="flex items-center justify-center">
      <Tooltip
        title={
          hasDpo
            ? t('dashboard.multitenanttable.dpo_assigned', 'DSB zugewiesen')
            : t('dashboard.multitenanttable.no_dpo_warning', 'Kein DSB zugewiesen')
        }
      >
        <IconButton onClick={onClick} size="small" disabled={user?.isReadOnly}>
          {hasDpo ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <WarningIcon color="warning" fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
}

export function MultiTenantTable() {
  const { t } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const history = useHistory();
  const location = useLocation();
  const { data: tenantsData, loading, error } = useUserTenantsQuery();

  const tasks = useTasks();

  const maxTenantsReached = (user?.companies.length || 1) >= (user?.maxTenants || 1);

  const { enqueueSnackbar } = useSnackbar();
  const [deleteTenant] = useDeleteTenantMutation();
  const [switchTenant, { loading: switchLoading }] = useSwitchTenantMutation();
  const [activateTenantBilling] = useActivateTenantBillingMutation();

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [mutateModelOpen, setMutateModelOpen] = useState(false);
  const [maxReachedModalOPen, setMaxReachedModalOpen] = useState(false);
  const [tenantForEdit, setTenantForEdit] = useState<{ id: string }>();
  const [deleteRow, setDeleteRow] = useState<MUIDataTableMeta | null>(null);
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);
  const [activateAlertOpen, setActivateAlertOpen] = useState(false);
  const [activateTarget, setActivateTarget] = useState<{ id: string; name: string } | null>(null);
  // Klaus-Kopplung (Addendum v1.2): verknüpfen / Verknüpfung aufheben.
  const [klausLinkTarget, setKlausLinkTarget] = useState<{ id: string; name: string } | null>(null);
  const [klausUnlinkTarget, setKlausUnlinkTarget] = useState<{ id: string; name: string } | null>(null);
  const [unlinkKlaus] = useUnlinkCompanyFromKlausMutation();

  const tenantData = user?.companies || [];

  // Check if DpoManaged feature is enabled
  const hasDpoFeature = user?.hasFeature('DpoManaged');
  // Only role Admins of the principal may switch a tenant from Demo to billing-active.
  const isAdmin = user?.role === UserRole.Admin;
  // Ohne eingerichtete Klaus-Kopplung keine Klaus-Spalte und keine Klaus-Aktionen.
  const klausSyncEnabled = useKlausSyncEnabled();

  const handleDelete = (tableMeta: MUIDataTableMeta) => {
    const tenantId = tableMeta.rowData[0];

    deleteTenant({
      variables: {
        companyId: tenantId,
      },
    })
      .then(() => {
        enqueueSnackbar(t('dashboard.multitenanttable.deletesuccess'), { variant: 'success' });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleAddTenant = () => {
    if (maxTenantsReached) {
      setMaxReachedModalOpen(true);
    } else {
      setMutateModelOpen(true);
    }
  };

  const handleActivateBilling = () => {
    if (!activateTarget) return;
    activateTenantBilling({ variables: { tenantCompanyId: activateTarget.id } })
      .then(() => {
        enqueueSnackbar(t('dashboard.multitenanttable.activate_success', 'Mandant wurde aktiviert.'), {
          variant: 'success',
        });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  // Die eigene Company einer Kanzlei wird nicht mit Klaus verknüpft, nur ihre Mandanten.
  const isKanzleiPrincipal = (company?: { isPrincipal: boolean }) =>
    Boolean(company?.isPrincipal && (user?.hasFeature('MultiTenancy') || hasDpoFeature));

  const handleUnlinkKlaus = () => {
    if (!klausUnlinkTarget) return;
    unlinkKlaus({ variables: { companyId: klausUnlinkTarget.id } })
      .then(() => {
        enqueueSnackbar('Klaus-Verknüpfung aufgehoben. Die Mitglieder bleiben in Klaus erhalten.', {
          variant: 'success',
        });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleEditTenant = (tableMeta: MUIDataTableMeta) => {
    setTenantForEdit(user?.companies.find((c) => c.id === tableMeta.rowData[0]));
    setMutateModelOpen(true);
  };

  const handleCloseModal = () => {
    setMutateModelOpen(false);
    setTenantForEdit(undefined);
  };

  const handleSwitchTenant = (tableMeta: MUIDataTableMeta) => {
    const tenantId = tableMeta.rowData[0];
    setSwitchingTenantId(tenantId);
    switchTenant({
      variables: {
        companyId: tenantId,
      },
    })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      })
      .finally(() => {
        setSwitchingTenantId(null);
      });
  };

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'id',
      label: 'ID',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'name',
      label: t('dashboard.multitenanttable.tenant'),
      options: {
        // Der Name ist der Wechsel: ein Klick öffnet den Mandanten. Der gerade
        // geöffnete trägt stattdessen "Geöffnet" (nicht "Aktiv", das ist der
        // Abrechnungsstatus).
        customBodyRender: (value, tableMeta) => {
          const companyId = tableMeta.rowData[0];
          const company = user?.companies.find((c) => c.id === companyId);
          const isOpen = user?.company?.id === companyId;
          const isSwitching = switchingTenantId === companyId;
          return (
            <div className="flex gap-2 items-center flex-wrap">
              {isOpen ? (
                <span>{value}</span>
              ) : (
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSwitchTenant(tableMeta);
                  }}
                  title={t('dashboard.multitenanttable.changetenant')}
                >
                  {value}
                </Link>
              )}
              {company?.isPrincipal && (
                <div className="bg-secondary-root rounded-md px-1 text-xs">{t('common.primary')}</div>
              )}
              {isOpen && (
                <Chip size="small" color="primary" label={t('dashboard.multitenanttable.opened', 'Geöffnet')} />
              )}
              {isSwitching && <Spinner size="small" />}
            </div>
          );
        },
      },
    },
    {
      name: 'city',
      label: t('dashboard.multitenanttable.location'),
      options: {
        customBodyRender: (value, tableMeta) => {
          const companyId = tableMeta.rowData[0];
          const company = user?.companies.find((c) => c.id === companyId);
          // tooltip should show full address with phone number
          const tooltip = (
            <div className="flex flex-col">
              <span>{company?.street}</span>
              <span>
                {company?.zip} {company?.city}
              </span>
              <span>{company?.phone}</span>
              <span>{company?.website}</span>
            </div>
          );

          return (
            <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={tooltip}>
              <div>{value}</div>
            </Tooltip>
          );
        },
      },
    },
    // DPO column - only show if DpoManaged feature is enabled
    ...(hasDpoFeature
      ? [
          {
            name: 'dpo',
            label: t('dashboard.multitenanttable.dpo', 'DSB'),
            options: {
              customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
                const companyId = tableMeta.rowData[0];
                return <DpoAssignmentCell companyId={companyId} onClick={() => handleEditTenant(tableMeta)} />;
              },
              sort: false,
            },
          },
        ]
      : []),
    // Billing status column (Aktiv/Demo) - only for the DpoManaged construct
    ...(hasDpoFeature
      ? [
          {
            name: 'billingStatus',
            label: t('dashboard.multitenanttable.billing_status', 'Status'),
            options: {
              sort: false,
              customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
                const companyId = tableMeta.rowData[0];
                const company = user?.companies.find((c) => c.id === companyId);
                if (!company || company.isPrincipal) return null;
                return company.billingActive ? (
                  <Chip size="small" color="success" label={t('dashboard.multitenanttable.status_active', 'Aktiv')} />
                ) : (
                  <Chip size="small" variant="outlined" label={t('dashboard.multitenanttable.status_demo', 'Demo')} />
                );
              },
            },
          },
        ]
      : []),
    // Klaus-Kopplung: mit welcher Klaus-Organisation der Mandant verknüpft ist.
    ...(klausSyncEnabled
      ? [
          {
            name: 'klaus',
            label: 'Klaus',
            options: {
              sort: false,
              customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
                const companyId = tableMeta.rowData[0];
                const company = user?.companies.find((c) => c.id === companyId);
                if (!company || isKanzleiPrincipal(company)) return null;
                const link = company.klausLink;
                // Kompakt wie die DSB-Spalte: nur ein Symbol, Details im Tooltip.
                const title = link
                  ? `Mit Klaus verknüpft${link.klausOrgName ? `: ${link.klausOrgName}` : ''} (seit ${new Date(
                      link.createdAt
                    ).toLocaleDateString('de-DE')}, ${link.createdByEmail})`
                  : 'Nicht mit Klaus verknüpft';
                return (
                  <div className="flex items-center justify-center">
                    <Tooltip title={title}>
                      {link ? (
                        <LinkIcon color="success" fontSize="small" />
                      ) : (
                        <LinkOffIcon color="disabled" fontSize="small" />
                      )}
                    </Tooltip>
                  </div>
                );
              },
            },
          },
        ]
      : []),
    {
      name: 'progress',
      label: t('common.progress'),
      options: {
        customHeadLabelRender: () => t('common.progress'),
        customBodyRenderLite: (dataIndex: number) => {
          const thisCompany = tenantData[dataIndex];
          if (loading) return <Skeleton variant="text" width={100} />;
          const company = tenantsData?.getUser.companies.find((c) => c.id === thisCompany.id);

          const allApps = (company?.topics || []).flatMap(
            (topic) =>
              topic.applications.filter((a) => [ApplicationType.Primary, ApplicationType.Secondary].includes(a.type)) ||
              []
          );
          const { percent, weighted, total } = computeWeightedProgress(allApps, tasks as any);
          const openReport = () => {
            if (!company) return;
            const sp = new URLSearchParams(location.search);
            sp.set('reportTenant', company.id);
            history.replace({ pathname: location.pathname, search: sp.toString() });
          };
          return (
            <div className="flex items-center gap-2 w-32" title={`${percent}%`}>
              <div className="flex-1 h-2 rounded bg-gray-200 overflow-hidden">
                <div
                  className={`${percent === 100 ? 'bg-success-500' : percent > 0 ? 'bg-primary-root' : 'bg-gray-400'} h-full transition-all`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className="text-[10px] tabular-nums text-gray-600">
                {weighted.toFixed(0)}/{total}
              </span>
              <IconButton size="small" aria-label="Report" onClick={openReport}>
                <AssessmentIcon fontSize="inherit" />
              </IconButton>
            </div>
          );
        },
        sort: false,
      },
    },

    {
      name: 'actions',
      label: 'Aktionen',
      options: {
        customHeadLabelRender: () => '',
        customBodyRender: (value, tableMeta) => {
          const companyId = tableMeta.rowData[0];
          const company = user?.companies.find((c) => c.id === companyId);
          const canActivate = hasDpoFeature && isAdmin && company && !company.isPrincipal && !company.billingActive;
          const canManageKlaus = klausSyncEnabled && isAdmin && company && !isKanzleiPrincipal(company);
          const items: ThreeDotItem[] = [
            {
              label: (
                <div className="flex gap-1 items-center">
                  <EditIcon fontSize="inherit" />
                  <span className="grow">{t('common.edit')}</span>
                </div>
              ),
              onClick: () => handleEditTenant(tableMeta),
            },
            ...(canActivate
              ? [
                  {
                    label: (
                      <div className="flex gap-1 items-center">
                        <CheckCircleIcon fontSize="inherit" />
                        <span className="grow">{t('dashboard.multitenanttable.activate', 'Aktivieren')}</span>
                      </div>
                    ),
                    onClick: () => {
                      setActivateTarget({ id: company!.id, name: company!.name || '' });
                      setActivateAlertOpen(true);
                    },
                  },
                ]
              : []),
            ...(canManageKlaus
              ? [
                  company!.klausLink
                    ? {
                        label: (
                          <div className="flex gap-1 items-center">
                            <LinkOffIcon fontSize="inherit" />
                            <span className="grow">Klaus-Verknüpfung aufheben</span>
                          </div>
                        ),
                        onClick: () => setKlausUnlinkTarget({ id: company!.id, name: company!.name || '' }),
                      }
                    : {
                        label: (
                          <div className="flex gap-1 items-center">
                            <LinkIcon fontSize="inherit" />
                            <span className="grow">Mit Klaus verknüpfen</span>
                          </div>
                        ),
                        onClick: () => setKlausLinkTarget({ id: company!.id, name: company!.name || '' }),
                      },
                ]
              : []),
            {
              label: (
                <div className="flex gap-1 items-center">
                  <DeleteIcon fontSize="inherit" />
                  <span className="grow">{t('components.ui.alert_dialog.actions.delete')}</span>
                </div>
              ),
              onClick: () => {
                setDeleteRow(tableMeta);
                setDeleteAlertOpen(true);
              },
            },
          ];
          return <ThreeDots items={items} />;
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    // sortOrder: {
    //   name: 'name',
    //   direction: 'asc',
    // },
    download: false,
    print: false,
    elevation: 0,

    selectableRows: 'none',
    selectableRowsHeader: false,
    // selectableRowsOnClick: true,
    // expandableRows: true,

    customToolbar: () => (
      <Tooltip title={maxTenantsReached ? t('dashboard.multitenanttable.maxtenantsreached') : undefined}>
        <span>
          <Button
            variant="outlined"
            onClick={handleAddTenant}
            startIcon={<AddIcon />}
            disabled={user?.isReadOnly}
            size="small"
          >
            {t('dashboard.training.employee.table.toolbar.actions.add')}
          </Button>
        </span>
      </Tooltip>
    ),
    search: false,
    filter: false,
    viewColumns: false,
    pagination: false,
    textLabels: {
      selectedRows: {
        text: 'Zeilen ausgewählt',
        delete: 'Löschen',
        deleteAria: 'Ausgewählte Zeilen löschen',
      },
      body: {
        noMatch: 'Keine Einträge vorhanden',
      },
    },
  };

  return (
    <div className="dtMultiTenantTable">
      {tenantData && <MUIDataTable columns={columns} data={tenantData} title="" options={options} />}
      <AlertDialog
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        message={
          <Trans t={t} i18nKey={'dashboard.multitenanttable.delete_alert'} values={{ value: deleteRow?.rowData[1] }}>
            Möchtest du den Mandanten <b>{deleteRow?.rowData[1]}</b> wirklich löschen? Dabei werden sämtliche verknüpfte
            Daten gelöscht. Dazu gehören beantwortete Fragebögen, hochgeladene Dateien und dokumentierte Prozesse.
          </Trans>
        }
        onAccept={() => deleteRow && handleDelete(deleteRow)}
      />
      <AlertDialog
        open={activateAlertOpen}
        setOpen={setActivateAlertOpen}
        message={
          <Trans t={t} i18nKey={'dashboard.multitenanttable.activate_alert'} values={{ value: activateTarget?.name }}>
            Möchten Sie den Mandanten <b>{activateTarget?.name}</b> aktivieren? Dieser Mandant wird dadurch
            abrechnungsrelevant. <b>Diese Aktion kann nicht rückgängig gemacht werden.</b>
          </Trans>
        }
        onAccept={handleActivateBilling}
        acceptText={t('dashboard.multitenanttable.activate', 'Aktivieren')}
      />
      <AlertDialog
        open={Boolean(klausUnlinkTarget)}
        setOpen={(open) => !open && setKlausUnlinkTarget(null)}
        message={
          <>
            Verknüpfung von <b>{klausUnlinkTarget?.name}</b> mit Klaus aufheben? Die Mitglieder bleiben in Klaus, werden
            aber nicht mehr aus Dieter übernommen.
          </>
        }
        onAccept={handleUnlinkKlaus}
        acceptText="Aufheben"
      />
      <KlausLinkDialog company={klausLinkTarget} onClose={() => setKlausLinkTarget(null)} />
      <MutateTenantModal open={mutateModelOpen} onClose={handleCloseModal} tenantId={tenantForEdit?.id} />
      <MaxReachedModal open={maxReachedModalOPen} onClose={() => setMaxReachedModalOpen(false)} />
      <MultiTenantInfo />
    </div>
  );
}
